import _ from "lodash";
import diff from "microdiff";
import { v4 as uuidv4 } from "uuid";
import { repeatTagWhitelist } from "@/constants";

/**
 * Initializes a custom group of WPD functions.
 */
function init(wpd, wpdWindow, wpdDocument, dataEntryVM) {
	wpd.custom = {};

	function getDataPointPosition(dataPoint) {
		// allow position-less data points to pass-through
		if (!dataPoint || (!dataPoint.x && !dataPoint.y)) {
			return null;
		}

		const rotation = wpd.graphicsWidget.getRotation();
		const { x, y } = wpd.graphicsWidget.getRotatedCoordinates(
			0,
			rotation,
			dataPoint.x,
			dataPoint.y,
		);
		const position = wpd.graphicsWidget.screenPx(x, y);

		// include wpd graphics container offset and scroll positions
		const graphicsContainer = wpdDocument.getElementById("graphicsContainer");
		const canvasDiv = wpdDocument.getElementById("canvasDiv");
		const marginLeftText = wpdWindow.getComputedStyle(canvasDiv).marginLeft;
		const marginLeft = parseInt(marginLeftText.replace("px", ""), 10);
		_.assign(position, {
			x: position.x - graphicsContainer.scrollLeft + marginLeft,
			y: position.y - graphicsContainer.scrollTop,
		});

		return position;
	}

	// For graph axes, augment a data point's metadata with empty-value broadcast
	// tag slots from the calibration. This produces the metadata object passed
	// to the per-point edit dialog: empty slots show up so the curator can type
	// values, but they aren't persisted on the underlying pixel until the
	// curator confirms (filterCapturedMetadata strips empty rows on round-trip,
	// so the broadcast just re-injects them next open).
	// Entries scoped to a specific datasetUuid only apply when datasetUuid
	// matches the point's dataset; entries with no datasetUuid apply graph-wide.
	// Branch-scoped wins over graph-wide for the same tag.
	function enrichMetadataWithBroadcastEmpties(metadata, currentAxis, dataset) {
		const axisType = wpd.custom.identifyAxis(currentAxis);
		if (axisType !== "xy" && axisType !== "bar") return metadata;
		const broadcastTags =
			currentAxis.getMetadata()?.axesLabelData?.broadcastTags;
		if (!Array.isArray(broadcastTags) || broadcastTags.length === 0) {
			return metadata;
		}
		const datasetUuid = dataset?.getMetadata?.()?.uuid;
		const applicable = broadcastTags.filter(
			(b) => !b.datasetUuid || b.datasetUuid === datasetUuid,
		);
		const emptyBroadcasts = applicable.filter((b) => b?.tagName && !b.value);
		if (emptyBroadcasts.length === 0) return metadata;

		const enriched = { ...(metadata || {}) };
		const numericKeys = Object.keys(enriched)
			.filter((k) => !isNaN(k))
			.map(Number);
		let nextIdx = numericKeys.length === 0 ? 0 : Math.max(...numericKeys) + 1;

		// Inject one empty fromBroadcast row per empty-broadcast configured.
		// Multiple empty rows for the same tag (or graph + branch empties on
		// the same tag) each surface as their own empty slot in the dialog.
		// Identity for "already has" is (tagName, broadcastValue,
		// datasetUuid) so re-opening the dialog doesn't double-inject the
		// same empty slot.
		for (const broadcast of emptyBroadcasts) {
			const tag = wpd.tagGroups.findTag({ name: broadcast.tagName });
			if (!tag) continue;
			const alreadyHas = Object.keys(enriched).some((key) => {
				if (isNaN(key)) return false;
				const e = enriched[key];
				if (!e?.fromBroadcast || e.tag?.name !== tag.name) return false;
				return (
					(e.broadcastValue ?? "") === (broadcast.value ?? "") &&
					(e.datasetUuid || null) === (broadcast.datasetUuid || null)
				);
			});
			if (alreadyHas) continue;
			enriched[nextIdx++] = {
				tag: { name: tag.name, uuid: tag.uuid },
				value: "",
				fromBroadcast: true,
				broadcastValue: broadcast.value,
				...(broadcast.datasetUuid
					? { datasetUuid: broadcast.datasetUuid }
					: {}),
			};
		}
		return enriched;
	}

	function getTagGroupData(index, dataPoint, axisType) {
		let activeTagGroup = wpd.tagGroups.activeTagGroup;
		const defaultTags = [];

		let tagGroup;
		let mode = "add";

		// check if the data point already has a tag group instance
		if (dataPoint.metadata?.tagGroup) {
			// edits
			mode = "edit";

			// load existing tag group instance, tag group, and tag if this is a
			// text axis
			if (axisType === "image") {
				wpd.tagGroups.loadDataPointTagGroup(index);
				activeTagGroup = wpd.tagGroups.activeTagGroup;
			}

			tagGroup = dataPoint.metadata.tagGroup;
			defaultTags.push(dataPoint.metadata[0].tag);
		} else if (activeTagGroup) {
			// text data adds
			tagGroup = _.pick(activeTagGroup, ["name", "uuid"]);

			// add the tag group instance uuid to the active tag group
			Object.assign(tagGroup, {
				instanceUuid: wpd.tagGroups.activeInstanceUuid,
			});

			// get the selected tag to collect data for
			if (wpd.tagGroups.activeTag) {
				defaultTags.push(wpd.tagGroups.activeTag);
			}
		} else {
			// something has gone wrong
			console.error("No active tag group or existing tag group on data point");
		}

		return {
			activeTagGroup,
			tagGroup,
			defaultTags,
			mode,
		};
	}

	function setActiveStudyFromAbbreviation([{ name: tagName }], metadata) {
		if (wpd.tagGroups.isKeyTag(tagName)) {
			const map = {
				[wpd.tagGroups.studyTagName]: "Study",
				[wpd.tagGroups.studyPartTagName]: "Part",
				[wpd.tagGroups.studyArmTagName]: "Arm",
				[wpd.tagGroups.studyPopulationTagName]: "Population",
				[wpd.tagGroups.studySubgroupTagName]: "Subgroup",
			};

			if (!_.isEmpty(metadata)) {
				wpd.tagGroups[`active${map[tagName]}Key`] = metadata[0].value;
			}
		}
	}

	function setActiveStudy(compositeKey) {
		const parsedKey = compositeKey
			? wpd.CompositeKeyUtils.parseCompositeKey(compositeKey)
			: {};
		wpd.tagGroups.activeStudyKey = parsedKey.study;
		wpd.tagGroups.activePartKey = parsedKey.studyPart;
		wpd.tagGroups.activeArmKey = parsedKey.studyArm;
		wpd.tagGroups.activePopulationKey = parsedKey.population;
		wpd.tagGroups.activeSubgroupKey = parsedKey.subgroup;
	}

	wpd.custom.openMetadataPreview = function (dataset, index) {
		const dataPoint = dataset.getPixel(index);
		const position = getDataPointPosition(dataPoint);
		dataEntryVM.openMetadataPreview({
			position,
			data: dataPoint.metadata,
		});
	};

	wpd.custom.openMetadataPreviewV2 = function (dataset, indexes) {
		const dataPoints = indexes.map((index) => dataset.getPixel(index));
		const position = getDataPointPosition(dataPoints[0]);
		const data = dataPoints
			.filter((dataPoint) => dataPoint?.metadata)
			.map(({ metadata }) => metadata);

		if (!position || _.isEmpty(data)) {
			return;
		}

		dataEntryVM.openMetadataPreview({ position, data });
	};

	wpd.custom.closeMetadataPreview = function () {
		dataEntryVM.closeMetadataPreview();
	};

	wpd.custom.openMetadataSelectDialog = function (overlappingPixels) {
		const position = getDataPointPosition(overlappingPixels[0].pixel);
		return dataEntryVM.openMetadataSelectDialog({
			position,
			data: overlappingPixels,
		});
	};

	/**
	 * Processes newly added or updated data point.
	 * @param {Object} dataset - Current WPD dataset object
	 * @param {Number} index   - Index of data point within given WPD dataset object
	 */
	wpd.custom.processDataPoint = async function (
		dataset,
		index,
		currentAxis,
		isBranchEdit,
		isBranchSelection,
		directTextSelect,
	) {
		const axisType = wpd.custom.identifyAxis(currentAxis);
		const dataPoint = dataset.getPixel(index);

		const position = getDataPointPosition(dataPoint);
		const { activeTagGroup, tagGroup, defaultTags, mode } = getTagGroupData(
			index,
			dataPoint,
			axisType,
		);

		// bar graph calculated value
		let calculatedValues = [];
		if (axisType === "bar") {
			const [barValue] = currentAxis.pixelToData(dataPoint.x, dataPoint.y);
			calculatedValues.push(barValue.toString());
		}

		if (dataPoint.metadata && !isBranchEdit && !isBranchSelection) {
			const parsedKey = wpd.CompositeKeyUtils.parseCompositeKey(
				dataPoint.metadata.compositeKey,
			);

			const activeCompositeKey = wpd.CompositeKeyUtils.generateCompositeKey(
				wpd.tagGroups.activeStudyKey,
				wpd.tagGroups.activePartKey,
				wpd.tagGroups.activeArmKey,
				wpd.tagGroups.activePopulationKey,
				wpd.tagGroups.activeSubgroupKey,
			);

			if (activeCompositeKey !== dataPoint.metadata.compositeKey) {
				wpd.tagGroups.selectStudyTreeBranch({
					study: parsedKey.study,
					part: parsedKey.studyPart,
					arm: parsedKey.studyArm,
					population: parsedKey.population,
					subgroup: parsedKey.subgroup,
				});
			}
		}

		// composite key before user inputs changes
		let oldCompositeKey;
		if (isBranchSelection) {
			const existingPixel = dataset
				.getAllPixels()
				.find(
					(pixel) =>
						!_.isEmpty(pixel.metadata) &&
						pixel.metadata.tagGroup.instanceUuid ===
							wpd.tagGroups.activeInstanceUuid &&
						!wpd.CompositeKeyUtils.isEmpty(pixel.metadata.compositeKey),
				);
			oldCompositeKey = existingPixel?.metadata?.compositeKey;
		} else {
			oldCompositeKey =
				isBranchEdit || !dataPoint.metadata
					? wpd.CompositeKeyUtils.generateCompositeKey(
							wpd.tagGroups.activeStudyKey,
							wpd.tagGroups.activePartKey,
							wpd.tagGroups.activeArmKey,
							wpd.tagGroups.activePopulationKey,
							wpd.tagGroups.activeSubgroupKey,
					  )
					: dataPoint.metadata.compositeKey;
		}

		const isImageTag = activeTagGroup && axisType === "image";
		const presetActiveStudy = isImageTag && !isBranchEdit && !isBranchSelection;
		if (presetActiveStudy) {
			setActiveStudy(oldCompositeKey);

			//TODO: Removing this as it doesn't seem necessary and caused visual bugs;
			// likely suspect if something breaks
			// wpd.tagGroups.refreshAll();
		}

		dataset.selectPixel(index);

		// wait for user input
		const rawMetadata = await dataEntryVM.captureMetadata({
			extractionCompositeKeys: wpd.tagGroups.compositeKeys,
			isBranchEdit,
			isBranchSelection,
			axisType,
			currentCompositeKey: oldCompositeKey,
			data: isBranchSelection
				? { 0: { tag: null, value: oldCompositeKey } }
				: enrichMetadataWithBroadcastEmpties(
						dataPoint.metadata,
						currentAxis,
						dataset,
				  ),
			defaultTags: isBranchSelection ? [] : defaultTags,
			limitTagGroups: tagGroup ? [tagGroup] : [],
			position,
			suggestionMap: wpd.custom.getSuggestionsByTag(),
			tagGroup,
			studyArmAbbreviations: wpd.tagGroups.getAllStudyArmKeys(),
			selectTextFunction: wpd.acquireData.selectText,
			directTextSelect,
			screenPositionFunction: getDataPointPosition,
			selectLocationFunction: wpd.acquireData.selectLocation,
		});

		if (rawMetadata?.location) {
			dataPoint.x = rawMetadata.location.x;
			dataPoint.y = rawMetadata.location.y;
			delete rawMetadata.location;
		}

		const metadata = wpd.custom.filterCapturedMetadata(rawMetadata);

		if (isImageTag && !presetActiveStudy) {
			if (isBranchSelection) {
				if (_.isEmpty(metadata)) {
					dataset.removePixelAtIndex(dataset.getAllPixels().length - 1);
				} else {
					wpd.custom.selectStudyBranch(dataset, metadata);
				}
			} else {
				setActiveStudyFromAbbreviation(defaultTags, metadata);
			}
		}

		if (!_.isEmpty(metadata)) {
			wpd.custom.fixCompositeKeysAndAbbreviationsV2(
				dataset,
				metadata,
				oldCompositeKey,
				isBranchEdit,
				isBranchSelection,
				mode,
			);
		}

		if (!isBranchSelection) {
			if (!_.isEmpty(metadata)) {
				if (
					wpd.custom.identifyAxis(currentAxis) === "xy" &&
					dataPoint.metadata
				) {
					// diff data point metadata
					const changes = diff(dataPoint.metadata, metadata);

					// filter out non-value diffs
					const valueChanges = changes.filter(({ path }) =>
						path.includes("value"),
					);

					// get metadata keys that are associated with changes
					const keysWithDiff = valueChanges.map(({ path }) => path[0]);

					// sync each key to account for changes in both x and y
					keysWithDiff.forEach((key) => {
						const data = metadata[key];

						// only sync tuple data points if the axis type is value
						if (data.axis && data.axisType === "value") {
							// sync shared values across point groups in the tuple
							wpd.pointGroups.syncTupleDataPointValue(
								dataset,
								currentAxis,
								index,
								data.axis,
								data.tag,
								data.value,
							);
						}
					});
				}

				// metadata collected, save to dataset
				wpd.custom.saveDataPoint(dataset, index, metadata);

				if (
					dataEntryVM.pointGroupOverhaul &&
					dataset.hasPointGroups() &&
					dataPoint.metadata?.pointGroupAxisDirection === "center"
				) {
					wpd.pointGroups.syncTupleIdsToCenterPoint(dataset, dataPoint);
				}
			} else {
				// no metadata, delete data point
				dataset.removePixelAtIndex(index);

				if (!dataEntryVM.pointGroupOverhaul) {
					// remove data point index references from tuples
					const tupleIndex = dataset.getTupleIndex(index);

					if (tupleIndex > -1) {
						dataset.removeFromTupleAt(tupleIndex, index);

						// update data point references in tuples
						dataset.refreshTuplesAfterPixelRemoval(index);

						// remove tuple if no point index references left in tuple
						if (dataset.isTupleEmpty(tupleIndex)) {
							dataset.removeTuple(tupleIndex);
						}
					}
				}

				if (isBranchEdit) {
					wpd.tagGroups.clearTags();
				}
			}
		}

		if (
			_.isEmpty(metadata) &&
			(isBranchEdit || isBranchSelection) &&
			wpd.tagGroups.isKeyTagGroup(wpd.tagGroups.activeTagGroup.name)
		) {
			wpd.tagGroups.reset();
		}

		// refresh tag group displays
		if (isImageTag) {
			// unset active tag uuid
			wpd.tagGroups.activeTag = null;

			// refresh button display, update collected data and studies
			wpd.tagGroups.refreshAll();
		} else {
			dataset.unselectAll();
		}

		// repaint the dataset
		wpd.custom.repaintDataPoints(dataset);

		// sync metadata keys on data set
		wpd.custom.syncMetadataKeys(dataset);

		await dataEntryVM.autoSaveMetadata();
	};

	wpd.custom.setCompositeKeyAtIndex = function (dataset, index, key) {
		let metadata = dataset.getPixel(index).metadata;
		Object.assign(metadata, {
			compositeKey: key,
		});

		dataset.setMetadataAt(index, metadata);
	};

	wpd.custom.updateAbbreviationValue = function (
		dataset,
		index,
		newValue,
		metadataIndex,
	) {
		let metadata = dataset.getPixel(index).metadata;
		Object.assign(metadata[metadataIndex], { value: newValue });
		dataset.setMetadataAt(index, metadata);
	};

	wpd.custom.switchTagGroup = function (dataset, targetTagGroup, tagActions) {
		if (!targetTagGroup || !tagActions) return;

		const sourceInstanceUuid = wpd.tagGroups.activeInstanceUuid;
		if (!sourceInstanceUuid) return;

		// Tags marked "move" survive the switch; everything else is dropped.
		const keepTagUuids = new Set(
			Object.entries(tagActions)
				.filter(([, action]) => action === "move")
				.map(([uuid]) => uuid),
		);

		const newInstanceUuid = uuidv4();

		const pixelsBefore = dataset
			.getAllPixels()
			.map((pixel, index) => ({ pixel, index }))
			.filter(
				({ pixel }) =>
					!!pixel.metadata &&
					!_.isEmpty(pixel.metadata) &&
					pixel.metadata.tagGroup &&
					pixel.metadata.tagGroup.instanceUuid === sourceInstanceUuid,
			);

		if (_.isEmpty(pixelsBefore)) return;

		const updatedPixels = [];
		pixelsBefore.forEach(({ pixel }) => {
			const newMetadata = { ...pixel.metadata };

			newMetadata.tagGroup = {
				name: targetTagGroup.name,
				uuid: targetTagGroup.uuid,
				instanceUuid: newInstanceUuid,
			};

			// Walk numeric rows; drop anything whose tag is not in keepTagUuids.
			// Skip key tags defensively. The button is hidden on key tag groups
			// so this should never matter, but a stray key tag row would
			// otherwise be silently deleted.
			Object.keys(newMetadata)
				.filter((key) => !isNaN(key))
				.forEach((key) => {
					const row = newMetadata[key];
					const tagName = row && row.tag && row.tag.name;
					const tagUuid = row && row.tag && row.tag.uuid;

					if (tagName && wpd.tagGroups.isKeyTag(tagName)) return;
					if (!tagUuid || !keepTagUuids.has(tagUuid)) {
						delete newMetadata[key];
					}
				});

			const newPixel = { ...pixel, metadata: newMetadata };
			wpd.custom.reindexPixelMetadata(newPixel);

			// Drop pixels that have no surviving numeric rows. Matches the
			// processDataPoint precedent for empty metadata.
			const hasRemainingRows = Object.keys(newPixel.metadata).some(
				(key) => !isNaN(key),
			);
			if (!hasRemainingRows) return;

			// Keep the plural tagGroups array (if present) consistent with the
			// new singular tagGroup. Text records don't populate per-row
			// tagGroup, so updatePointTagGroups would produce an empty array;
			// rebuild explicitly instead.
			if (Array.isArray(newPixel.metadata.tagGroups)) {
				newPixel.metadata.tagGroups = [{ ...newPixel.metadata.tagGroup }];
			}

			updatedPixels.push(newPixel);
		});

		// Remove source-instance pixels, then re-add mutated copies. Mirrors
		// selectStudyBranch's "delete and re-add" pattern so downstream events
		// (point.delete + point.add) fire normally.
		wpd.tagGroups.clearTags(false, dataset, sourceInstanceUuid);

		updatedPixels.forEach((pixel) => {
			dataset.addPixel(pixel.x, pixel.y, pixel.metadata);
			const maxIndex = dataset.getAllPixels().length - 1;
			wpd.custom.saveDataPoint(dataset, maxIndex, pixel.metadata);
		});

		// Refresh the study tree so the new instance shows up under the
		// target tag group. The caller is responsible for focusing it.
		wpd.tagGroups.setStudyKeys();

		return newInstanceUuid;
	};

	wpd.custom.selectStudyBranch = function (dataset, metadata) {
		if (!metadata || !metadata.compositeKey) return;

		const pixelsBefore = dataset
			.getAllPixels()
			.map((pixel, index) => ({ pixel, index }))
			.filter(
				({ pixel }) =>
					!!pixel.metadata &&
					!_.isEmpty(pixel.metadata) &&
					pixel.metadata.tagGroup.instanceUuid ===
						wpd.tagGroups.activeInstanceUuid &&
					!wpd.tagGroups.isKeyTag(pixel.metadata["0"].tag.name),
			);

		if (!_.isEmpty(pixelsBefore)) {
			wpd.tagGroups.loadDataPointTagGroup(pixelsBefore[0].index);
		}

		wpd.tagGroups.clearTags();
		setActiveStudy(metadata.compositeKey);
		const updatedPixels = pixelsBefore.map(({ pixel }) => {
			return {
				...pixel,
				metadata: {
					...pixel.metadata,
					compositeKey: metadata.compositeKey,
					tagGroup: {
						...pixel.metadata.tagGroup,
						instanceUuid: wpd.tagGroups.activeInstanceUuid,
					},
				},
			};
		});

		updatedPixels.forEach((pixel) => {
			dataset.addPixel(pixel.x, pixel.y, pixel.metadata);
			const maxIndex = dataset.getAllPixels().length - 1;
			wpd.custom.saveDataPoint(dataset, maxIndex, pixel.metadata);
		});

		wpd.tagGroups.setStudyKeys();
	};

	wpd.custom.fixCompositeKeysAndAbbreviationsV2 = function (
		dataset,
		metadata,
		oldCompositeKey,
		isBranchEdit,
		isBranchSelection,
		mode,
	) {
		const newCompositeKey = wpd.CompositeKeyUtils.generateCompositeKey(
			wpd.tagGroups.activeStudyKey,
			wpd.tagGroups.activePartKey,
			wpd.tagGroups.activeArmKey,
			wpd.tagGroups.activePopulationKey,
			wpd.tagGroups.activeSubgroupKey,
		);

		// Update the composite key for the current pixel
		metadata.compositeKey = newCompositeKey;

		if (
			(!isBranchEdit && !isBranchSelection) ||
			!oldCompositeKey ||
			oldCompositeKey === newCompositeKey
		)
			return;

		const activeInstanceUuid = wpd.tagGroups.activeInstanceUuid;

		const pixels = dataset.getAllPixels().filter((pixel) => !!pixel.metadata);

		// Update composite keys for the rest of the tag group instance
		pixels.forEach((pixel, i) => {
			if (
				!_.isEmpty(pixel.metadata) &&
				pixel.metadata.tagGroup.instanceUuid === activeInstanceUuid
			) {
				wpd.custom.setCompositeKeyAtIndex(dataset, i, newCompositeKey);
			}
		});

		if (isBranchSelection || mode !== "edit") {
			return;
		}

		const newValue = metadata[0].value;
		const tagName = metadata[0].tag.name;

		const oldParsedKey =
			wpd.CompositeKeyUtils.parseCompositeKey(oldCompositeKey);

		const datasets = wpd.appData.getPlotData().getDatasets();
		datasets.forEach((plotDataset) => {
			const datasetPixels = plotDataset.getAllPixels();
			const datasetMetadata = plotDataset.getMetadata();
			const axis = wpd.appData.getPlotData().getAxesForDataset(plotDataset);
			const type = wpd.custom.identifyAxis(axis);

			// Fix any table header data referencing the updated composite key
			if (type === "table") {
				this.fixTableCompositeKeysAndAbbreviations(
					plotDataset,
					oldCompositeKey,
					newValue,
					tagName,
				);
			}

			// Fix any graph calibration data referencing the updated composite key
			if (["xy", "bar", "polar", "ternary"].includes(type)) {
				this.fixGraphCompositeKeysAndAbbreviations(
					plotDataset,
					datasetMetadata,
					oldCompositeKey,
					newValue,
					tagName,
				);
			}

			// Fix any data points referencing the updated composite key
			datasetPixels.forEach((pixel, i) => {
				if (!pixel.metadata) return;

				const dsMetadata = pixel.metadata;
				let mutated = false;

				const ownIsChild =
					dsMetadata.compositeKey &&
					wpd.CompositeKeyUtils.isChildKey(
						dsMetadata.compositeKey,
						oldCompositeKey,
					);

				if (ownIsChild) {
					dsMetadata.compositeKey = wpd.CompositeKeyUtils.updateKey(
						dsMetadata.compositeKey,
						tagName,
						newValue,
					);
					mutated = true;

					const mdIndex = Object.keys(dsMetadata)
						.filter((key) => !isNaN(key))
						.find((key) => {
							const md = dsMetadata[key];
							const mdValue = md.value;
							const mdTag = md.tag.name;
							return (
								(tagName === wpd.CompositeKeyUtils.studyTag &&
									mdTag === wpd.CompositeKeyUtils.studyTag &&
									mdValue === oldParsedKey.study) ||
								(tagName === wpd.CompositeKeyUtils.studyPartTag &&
									mdTag === wpd.CompositeKeyUtils.studyPartTag &&
									mdValue === oldParsedKey.studyPart) ||
								(tagName === wpd.CompositeKeyUtils.studyArmTag &&
									mdTag === wpd.CompositeKeyUtils.studyArmTag &&
									mdValue === oldParsedKey.studyArm) ||
								(tagName === wpd.CompositeKeyUtils.populationTag &&
									mdTag === wpd.CompositeKeyUtils.populationTag &&
									mdValue === oldParsedKey.population) ||
								(tagName === wpd.CompositeKeyUtils.subgroupTag &&
									mdTag === wpd.CompositeKeyUtils.subgroupTag &&
									mdValue === oldParsedKey.subgroup)
							);
						});

					if (mdIndex) {
						Object.assign(dsMetadata[mdIndex], { value: newValue });
					}
				}

				// Reference branch tags. Renaming a branch must propagate to
				// any cell that references the renamed branch, otherwise the
				// Reference values go stale and point at a now-nonexistent
				// study/arm. Iterate per-row instead of gating on the
				// cell-level `referenceCompositeKey` field: multi-TG table
				// cells can carry one Reference branch tag per tab (each
				// potentially pointing at a different branch), so each row's
				// value has to be checked on its own.
				let lastUpdatedRefValue = null;
				Object.keys(dsMetadata)
					.filter((key) => !isNaN(key))
					.forEach((key) => {
						const md = dsMetadata[key];
						if (
							!wpd.CompositeKeyUtils.isReferenceBranchTag(md?.tag?.name) ||
							!md.value ||
							!wpd.CompositeKeyUtils.isChildKey(md.value, oldCompositeKey)
						) {
							return;
						}
						const updated = wpd.CompositeKeyUtils.updateKey(
							md.value,
							tagName,
							newValue,
						);
						md.value = updated;
						lastUpdatedRefValue = updated;
						mutated = true;
					});

				// Keep `referenceCompositeKey` (the legacy cell-level field)
				// roughly in sync. It's vestigial for correctness now -- the
				// per-row scan above is authoritative -- but other code may
				// still read it. Mirror whichever value was updated last,
				// matching the existing save-flow convention.
				if (lastUpdatedRefValue !== null) {
					dsMetadata.referenceCompositeKey = lastUpdatedRefValue;
				}

				if (mutated) {
					plotDataset.setMetadataAt(i, dsMetadata);
				}
			});
		});
	};

	wpd.custom.fixTableCompositeKeysAndAbbreviations = function (
		dataset,
		oldKey,
		newValue,
		tagName,
	) {
		const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);
		const axisMetadata = axis.getMetadata();
		const oldParsedKey = wpd.CompositeKeyUtils.parseCompositeKey(oldKey);
		const calibration = axisMetadata?.calibration;
		const updateHeaderBranchValues = (header) => {
			if (!header) return;

			const ownIsChild =
				header.compositeKey &&
				wpd.CompositeKeyUtils.isChildKey(header.compositeKey, oldKey);

			if (ownIsChild) {
				header.compositeKey = wpd.CompositeKeyUtils.updateKey(
					header.compositeKey,
					tagName,
					newValue,
				);

				Object.keys(header)
					.filter((mdIndex) => !isNaN(mdIndex))
					.forEach((mdIndex) => {
						const md = header[mdIndex];
						const mdValue = md.value;
						const mdTag = md.tag.name;
						const shouldUpdateValue =
							(tagName === wpd.CompositeKeyUtils.studyTag &&
								mdTag === wpd.CompositeKeyUtils.studyTag &&
								mdValue === oldParsedKey.study) ||
							(tagName === wpd.CompositeKeyUtils.studyPartTag &&
								mdTag === wpd.CompositeKeyUtils.studyPartTag &&
								mdValue === oldParsedKey.studyPart) ||
							(tagName === wpd.CompositeKeyUtils.studyArmTag &&
								mdTag === wpd.CompositeKeyUtils.studyArmTag &&
								mdValue === oldParsedKey.studyArm) ||
							(tagName === wpd.CompositeKeyUtils.populationTag &&
								mdTag === wpd.CompositeKeyUtils.populationTag &&
								mdValue === oldParsedKey.population) ||
							(tagName === wpd.CompositeKeyUtils.subgroupTag &&
								mdTag === wpd.CompositeKeyUtils.subgroupTag &&
								mdValue === oldParsedKey.subgroup);
						if (shouldUpdateValue) {
							md.value = newValue;
						}
					});
			}

			// Reference branch tag values on this header. Iterate per row
			// (rather than gating on the header-level `referenceCompositeKey`
			// field) so a header carrying more than one Reference branch tag
			// -- possible if multiple tag groups contribute reference rows --
			// still updates each row whose value points at the renamed
			// branch. The header-level field stays mirrored to whichever
			// value was updated last, matching prior behavior.
			let lastUpdatedRefValue = null;
			Object.keys(header)
				.filter((mdIndex) => !isNaN(mdIndex))
				.forEach((mdIndex) => {
					const md = header[mdIndex];
					if (
						!wpd.CompositeKeyUtils.isReferenceBranchTag(md?.tag?.name) ||
						!md.value ||
						!wpd.CompositeKeyUtils.isChildKey(md.value, oldKey)
					) {
						return;
					}
					md.value = wpd.CompositeKeyUtils.updateKey(
						md.value,
						tagName,
						newValue,
					);
					lastUpdatedRefValue = md.value;
				});
			if (lastUpdatedRefValue !== null) {
				header.referenceCompositeKey = lastUpdatedRefValue;
			}
		};

		if (calibration && calibration.cols) {
			(calibration.cols.headers ?? []).forEach(updateHeaderBranchValues);
		}

		if (calibration && calibration.rows) {
			(calibration.rows.headers ?? []).forEach(updateHeaderBranchValues);
		}

		axis.setMetadata(axisMetadata);
	};

	wpd.custom.fixGraphCompositeKeysAndAbbreviations = function (
		dataset,
		metadata,
		oldKey,
		newValue,
		tagName,
	) {
		const compositeKey = metadata.studyData?.compositeKey;
		if (
			!compositeKey ||
			!wpd.CompositeKeyUtils.isChildKey(compositeKey, oldKey)
		) {
			return;
		}

		const updatedCompositeKey = wpd.CompositeKeyUtils.updateKey(
			compositeKey,
			tagName,
			newValue,
		);

		metadata.studyData.compositeKey = updatedCompositeKey;
		const previousValue = wpd.CompositeKeyUtils.getValueAtBranch(
			compositeKey,
			tagName,
		);
		if (metadata.name === previousValue) {
			metadata.name = newValue;
		}
		dataset.setMetadata(metadata);
	};

	/**
	 * Processes newly added or updated data points.
	 * @param {Object} dataset - Current WPD dataset object
	 * @param {Number} indexes - Indexes of data points within given WPD dataset object
	 */
	wpd.custom.processDataPoints = async function (
		dataset,
		indexes,
		isBranchEdit,
	) {
		// get existing non-numeric values for auto-suggest
		const suggestions = wpd.custom.getExistingNonNumericValues();

		const newMetadata = await dataEntryVM.captureMetadata({
			isBranchEdit,
			position: null,
			data: null,
			suggestions,
			studyArmAbbreviations: wpd.tagGroups.getAllStudyArmKeys(),
		});

		if (newMetadata.location) {
			// not supported
			delete newMetadata.location;
		}

		if (!_.isEmpty(newMetadata)) {
			wpd.custom.saveDataPoints(dataset, indexes, newMetadata);

			// sync metadata keys on data set
			wpd.custom.syncMetadataKeys(dataset);
		}

		return new Promise((resolve) => resolve());
	};

	/**
	 * Triggers repainting of data points.
	 * @param {Object} dataset - Current WPD dataset object
	 */
	wpd.custom.repaintDataPoints = function (dataset) {
		wpd.graphicsWidget.forceHandlerRepaint();
		wpd.dataPointCounter.setCount(dataset.getCount());
	};

	/**
	 * Writes metadata to a data point. Overwrites.
	 * @param {Object} dataset  - Current WPD dataset object
	 * @param {Number} index    - Index of data point within given WPD dataset object
	 * @param {Object} metadata - Metadata object to write to data point
	 */
	wpd.custom.saveDataPoint = function (dataset, index, metadata) {
		// store metadata in data point
		dataset.setMetadataAt(index, metadata);
	};

	/**
	 * Writes metadata to data points. Overwrites.
	 * @param {Object} dataset  - Current WPD dataset object
	 * @param {Number} indexes  - Indexes of data points within given WPD dataset object
	 * @param {Object} metadata - Metadata object to write to data points
	 */
	wpd.custom.saveDataPoints = function (dataset, indexes, newMetadata) {
		// multiple points metadata dialog does not return uuids
		_.forEach(indexes, (index) => {
			let metadata = dataset.getPixel(index).metadata;

			// leave uuid on each point intact if it already exists
			if (!metadata || _.isEmpty(metadata)) {
				// point does not have any metadata, assign a UUID
				metadata = {
					uuid: uuidv4(),
				};
			}

			wpd.custom.saveDataPoint(dataset, index, {
				...newMetadata,
				uuid: metadata.uuid,
			});
		});
	};

	/**
	 * Keeps metadata keys on the dataset up-to-date.
	 * @param  {Object} dataset - Current WPD dataset object
	 */
	wpd.custom.syncMetadataKeys = function (dataset) {
		// gather all data point metadata objects in an array
		const allMetadata = _.map(dataset.getAllPixels(), "metadata");

		// collect arrays of keys of each metadata object into an array
		const collectedKeys = _.map(allMetadata, (metadata) => _.keys(metadata));

		// find the unique union between all arrays of keys
		const metadataKeys = _.union(...collectedKeys).sort();

		// maintain label order
		if (_.includes(metadataKeys, "label")) {
			_.pull(metadataKeys, "label");
			metadataKeys.unshift("label");
		}

		// maintain overrides order
		if (_.includes(metadataKeys, "overrides")) {
			_.pull(metadataKeys, "overrides");
			metadataKeys.push("overrides");
		}

		// set metadata keys on data set
		dataset.setMetadataKeys(metadataKeys);
	};

	/**
	 * Determines axis type of a given WPD axis object.
	 * @param  {Object} axis - WPD axis object
	 * @return {String}      - Axis type of the given WPD axis
	 */
	wpd.custom.identifyAxis = function (axis) {
		let axisType;

		switch (Object.getPrototypeOf(axis)) {
			case Object.getPrototypeOf(new wpd.BarAxes()):
				axisType = "bar";
				break;
			case Object.getPrototypeOf(new wpd.ImageAxes()):
				// table axes are image axes under the hood
				if (axis.getMetadata().table) {
					axisType = "table";
				} else {
					axisType = "image";
				}
				break;
			case Object.getPrototypeOf(new wpd.MapAxes()):
				axisType = "map";
				break;
			case Object.getPrototypeOf(new wpd.PolarAxes()):
				axisType = "polar";
				break;
			case Object.getPrototypeOf(new wpd.TernaryAxes()):
				axisType = "ternary";
				break;
			case Object.getPrototypeOf(new wpd.XYAxes()):
				axisType = "xy";
				break;
			default:
				axisType = "unknown";
		}

		return axisType;
	};

	/**
	 * Determines if the given WPD axis object is a graph axis.
	 * @param  {Object} axis - WPD axis object
	 * @return {Boolean}     - Whether or not axis is a graph axis
	 */
	wpd.custom.isGraphAxis = (axis) => {
		return _.includes(
			["xy", "bar", "polar", "ternary"],
			wpd.custom.identifyAxis(axis),
		);
	};

	/**
	 * Determines whether the given axis is a table axis.
	 * @param  {Object} axis - WPD axis object
	 * @return {Boolean}     - Whether or not axis is a table axis
	 */
	wpd.custom.isTableAxis = (axis) => {
		return wpd.custom.identifyAxis(axis) === "table";
	};

	/**
	 * Determines whether the given axis is a text/image axis.
	 * @param  {Object} axis - WPD axis object
	 * @return {Boolean}     - Whether or not axis is a text/image axis
	 */
	wpd.custom.isTextAxis = (axis) => {
		return wpd.custom.identifyAxis(axis) === "image";
	};

	/**
	 * Returns a set of unique non-numeric metadata values currently used.
	 * @return {Array} - A set of unique non-numeric metadata values
	 */
	wpd.custom.getExistingNonNumericValues = function () {
		const existingValues = new Set();

		const datasets = wpd.appData.getPlotData().getDatasets();

		// if tag groups are present, limit to the active tag
		let activeTagUuid = "";
		if (wpd.tagGroups.activeTag) {
			activeTagUuid = wpd.tagGroups.activeTag.uuid;
		}

		// loop through every dataset
		datasets.forEach((dataset) => {
			// loop through each existing data point
			dataset.getAllPixels().forEach((dataPoint) => {
				Object
					// filter out non-numeric keys, numeric keys are where metadata values and tags are stored
					.values(_.omitBy(dataPoint.metadata, wpd.utils.isWord))
					// filter out numeric values
					.filter((data) => {
						return isNaN(data.value) && data.tag?.uuid === activeTagUuid;
					})
					.forEach(({ value }) => existingValues.add(value));
			});
		});

		return Array.from(existingValues);
	};

	wpd.custom.getSuggestionsByTag = function () {
		const suggestionMap = new Map();
		const activeAxis = wpd.tree.getActiveAxes();
		const activeDataset = wpd.tree.getActiveDataset();
		const currentAxis =
			activeAxis ?? activeDataset
				? wpd.appData.getPlotData().getAxesForDataset(activeDataset)
				: null;

		const datasets = wpd.appData.getPlotData().getDatasets();
		datasets.forEach((dataset) => {
			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);
			const axisType = wpd.custom.identifyAxis(axis);
			dataset.getAllPixels().forEach((dataPoint) => {
				Object.values(_.omitBy(dataPoint.metadata, wpd.utils.isWord))
					.filter((data) => data.tag?.uuid)
					.forEach((data) => {
						const tagUuid = data.tag.uuid;
						if (!suggestionMap.has(tagUuid)) {
							suggestionMap.set(tagUuid, []);
						}

						const existingValues = suggestionMap.get(tagUuid);
						const alreadyExists = existingValues.some((value) => {
							value.label === data.value;
						});
						if (!alreadyExists) {
							const isAxisTypeMatch =
								currentAxis &&
								axis &&
								axisType === wpd.custom.identifyAxis(currentAxis);
							if (isAxisTypeMatch) {
								existingValues.push({
									isGraphMatch:
										["xy", "bar"].includes(axisType) &&
										currentAxis &&
										axis &&
										axis.name === currentAxis.name,
									tagGroupUuid: dataPoint.metadata?.tagGroup?.uuid,
									value: data.value,
								});
							}
						}
					});
			});
		});

		return suggestionMap;
	};

	/**
	 * Generates a new axis name, given a base name. Will enumerate the name based on existing axes and base name.
	 * @param  {String}             - Axis base name
	 * @param  {Boolean} includeOne - Include "1" at the end of the first occurrence
	 * @return {String}             - A new axis name
	 */
	wpd.custom.makeAxisName = function (baseName, includeOne) {
		// get all existing axis names
		const existingAxisNames = wpd.appData.getPlotData().getAxesNames();

		// avoid conflict with an existing name
		let index = 2;
		let axisName = baseName;

		// if include "1" is true, add the "1"
		if (includeOne) {
			axisName = `${axisName} 1`;
		}

		while (existingAxisNames.indexOf(axisName) >= 0) {
			axisName = `${baseName} ${index}`;
			index++;
		}

		return axisName;
	};

	/**
	 * DEPRECATED
	 * Generates a new dataset name, given a base name. Will enumerate the name based on existing datasets and base name.
	 * @param  {String}  baseName   - Dataset base name
	 * @param  {Boolean} includeOne - Include "1" at the end of the first occurrence
	 * @return {String}             - A new dataset name
	 */
	wpd.custom.makeDatasetName = function (baseName, includeOne) {
		const existingDatasetNames = wpd.appData.getPlotData().getDatasetNames();

		// avoid conflict with an existing name
		let index = 2;
		let datasetName = baseName;

		// if include "1" is true, add the "1"
		if (includeOne) {
			datasetName = `${datasetName} 1`;
		}

		while (existingDatasetNames.indexOf(datasetName) >= 0) {
			datasetName = `${baseName} ${index}`;
			index++;
		}

		return datasetName;
	};

	/**
	 * Deletes given axis and associated datasets from all collections.
	 */
	wpd.custom.deleteAxisDeep = function (axis) {
		// clear active axis and dataset
		wpd.tree.setActiveAxis(null);
		wpd.tree.setActiveDataset(null);

		const plotData = wpd.appData.getPlotData();

		// get all datasets associated with the axis
		const datasets = plotData.getDatasetsForAxis(axis);

		// get xy label axis if it exists
		const labelAxis = plotData
			.getAxesColl()
			.find((a) => a.name === `${axis.name} Labels`);
		const labelDataset = plotData
			.getDatasets()
			.find((d) => d.name === `${axis.name} Labels`);

		// delete axes and datasets from plot data
		plotData.deleteAxes(axis);
		plotData.deleteAxes(labelAxis);
		for (const dataset of datasets) {
			plotData.deleteDataset(dataset);
		}
		plotData.deleteDataset(labelDataset);

		// delete axes and datasets from file manager
		const fileManager = wpd.appData.getFileManager();
		fileManager.deleteAxesFromCurrentFile([axis, labelAxis]);
		fileManager.deleteDatasetsFromCurrentFile([...datasets, labelDataset]);

		// delete active axis from page manager
		if (wpd.appData.isMultipage()) {
			const pageManager = wpd.appData.getPageManager();
			pageManager.deleteAxesFromCurrentPage([axis, labelAxis]);
			pageManager.deleteDatasetsFromCurrentPage([...datasets, labelDataset]);
		}
	};

	/**
	 * Deletes given dataset from all collections.
	 */
	wpd.custom.deleteDatasetDeep = function (dataset) {
		// clear active dataset
		wpd.tree.setActiveDataset(null);

		const plotData = wpd.appData.getPlotData();

		plotData.deleteDataset(dataset);

		// delete axes and datasets from file manager
		const fileManager = wpd.appData.getFileManager();
		fileManager.deleteDatasetsFromCurrentFile([dataset]);

		// delete active axis from page manager
		if (wpd.appData.isMultipage()) {
			const pageManager = wpd.appData.getPageManager();
			pageManager.deleteDatasetsFromCurrentPage([dataset]);
		}
	};

	wpd.custom.filterCapturedMetadata = function (metadata) {
		// guard against empty metadata
		if (!metadata) {
			return {};
		}

		let startIndex = 0;
		return Object.entries(metadata).reduce((acc, [key, value]) => {
			if (wpd.utils.isWord(value, key)) {
				acc[key] = value;
			} else if (
				value.tag &&
				!_.isEmpty(value.tag) &&
				!wpd.utils.isEmptyValue(value.value) &&
				!value.headerTag
			) {
				acc[startIndex++] = value;
			}
			return acc;
		}, {});
	};

	wpd.custom.reindexPixelMetadata = function (pixel) {
		const numericKeys = Object.keys(pixel.metadata)
			.filter((key) => !isNaN(key))
			.map(Number)
			.sort((a, b) => a - b);

		const newMetadata = {};
		numericKeys.forEach((oldKey, newIndex) => {
			newMetadata[newIndex] = pixel.metadata[oldKey];
		});

		pixel.metadata = {
			...Object.fromEntries(
				Object.entries(pixel.metadata).filter(([key]) => isNaN(key)),
			),
			...newMetadata,
		};
	};

	wpd.custom.updatePointTagGroups = (metadata) => {
		const existingMap = metadata.tagGroups
			? metadata.tagGroups.reduce((acc, tagGroup) => {
					acc.set(tagGroup.uuid, tagGroup);
					return acc;
			  }, new Map())
			: new Map();

		const tagGroups = Object.entries(metadata).reduce((acc, [key, value]) => {
			if (!wpd.utils.isWord(value, key)) {
				if (!value.tagGroup) {
					return acc;
				}
				const existing = existingMap.get(value.tagGroup.uuid);
				acc.set(
					value.tagGroup.uuid,
					existing
						? { ...existing }
						: {
								...value.tagGroup,
								instanceUuid: uuidv4(),
						  },
				);
			}
			return acc;
		}, new Map());

		const uniqueTagGroups = Array.from(tagGroups.values());

		metadata.tagGroups = uniqueTagGroups;
	};

	wpd.custom.filterDuplicateCellData = (metadata) => {
		const nonHeaderTags = Object.keys(metadata)
			.filter((key) => !isNaN(key) && !metadata[key].headerTag)
			.reduce((acc, key) => {
				const tagGroupUuid =
					metadata[key].tagGroup?.uuid ?? metadata.tagGroup?.uuid;
				const tagUuid = metadata[key].tag?.uuid;
				if (tagGroupUuid && tagUuid) {
					if (!acc[tagGroupUuid]) acc[tagGroupUuid] = [];
					acc[tagGroupUuid].push(tagUuid);
				}
				return acc;
			}, {});

		// filter and reindex numeric keys
		const whitelistTags = repeatTagWhitelist;
		const numericEntries = Object.entries(metadata)
			.filter(([key]) => !isNaN(key))
			.filter(([_, px]) => {
				return (
					!px.headerTag ||
					!nonHeaderTags[
						px.tagGroup?.uuid ?? metadata.tagGroup?.uuid
					]?.includes(px.tag.uuid) ||
					whitelistTags.includes(px.tag.name)
				);
			})
			.map(([_, px], index) => [index, px]);

		// Remove all numeric keys from metadata
		Object.keys(metadata)
			.filter((key) => !isNaN(key))
			.forEach((key) => {
				delete metadata[key];
			});

		// Add filtered/reindexed numeric keys back
		numericEntries.forEach(([index, px]) => {
			metadata[index] = px;
		});
	};

	wpd.custom.undo = function () {
		dataEntryVM.undo();
	};

	wpd.custom.redo = function () {
		dataEntryVM.redo();
	};

	/**
	 * Get default decimal points to round to
	 */
	wpd.custom.defaultDecimalPlaces = 5;
}

export { init };
